import { useRef } from 'react';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { ADMISSION_SUPPORT_ROLES } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { FormSection } from '@/components/ui/form-screen';
import { Button, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { careRoleLabel } from './AdmissionPatientBar';
import { useCareTeamCandidates } from './queries';
import { ADMIT_CARE_TEAM_ROWS, choosableDoctors, type DraftCareMember } from './admit-care-team';

export function AdmitCareTeamSection({ primaryId, value, onChange }: { primaryId: string; value: DraftCareMember[]; onChange: (next: DraftCareMember[]) => void }) {
  const candidates = useCareTeamCandidates(undefined, true);
  const counter = useRef(0);
  const doctors = candidates.data ?? [];
  const update = (key: string, patch: Partial<DraftCareMember>) => onChange(value.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const add = () => {
    counter.current += 1;
    onChange([...value, { key: `member-${counter.current}`, userId: '', role: 'co_consultant' }]);
  };

  return (
    <FormSection title={tr('ipd', 'ws_care_team')} description={tr('ipd', 'ws_admit_care_team_hint')} icon={Users} columns={1}>
      {value.length > 0 && (
        <ul className="space-y-2">
          {value.map((row, index) => (
            <li key={row.key} className="flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center">
              <Select
                value={row.userId}
                onChange={(event) => update(row.key, { userId: event.target.value })}
                aria-label={tr('ipd', 'ws_doctor_n', { n: index + 1 })}
                className="sm:flex-1"
              >
                <option value="">{candidates.isLoading ? tr('ipd', 'loading') : tr('ipd', 'ws_choose_a_doctor')}</option>
                {choosableDoctors(doctors, value, primaryId, row.userId).map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.designation ? `${doctor.name} · ${doctor.designation}` : doctor.name}
                  </option>
                ))}
              </Select>
              <ChoiceButtons
                value={row.role}
                onChange={(role) => update(row.key, { role })}
                label={tr('ipd', 'ws_role')}
                options={ADMISSION_SUPPORT_ROLES.map((role) => ({ value: role, label: careRoleLabel(role) }))}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => onChange(value.filter((other) => other.key !== row.key))} aria-label={tr('ipd', 'ws_remove')}>
                <Trash2 className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={value.length >= ADMIT_CARE_TEAM_ROWS}>
          <UserPlus className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_add_doctor')}
        </Button>
      </div>
    </FormSection>
  );
}
